<template>
  <AdminLayout>
    <div class="card-management">
      <h2>卡密管理</h2>

      <!-- 激活记录 -->
      <section class="activations-section">
        <h3>激活记录</h3>

        <div class="activation-search flex gap-sm align-items-center">
          <input
            type="email"
            v-model="searchEmail"
            placeholder="按邮箱搜索激活记录"
            @keyup.enter="searchActivationsByEmail"
            class="form-control"
            style="flex: 1; max-width: 300px;"
          />
          <button @click="searchActivationsByEmail" class="btn btn-primary">
            搜索
          </button>
          <button @click="clearActivationSearch" class="btn btn-secondary">
            清空
          </button>
        </div>

        <AdminTable
          :columns="activationColumns"
          :data="activations"
          :total="totalActivations"
          :current-page="activationCurrentPage"
          :page-size="activationPageSize"
          :row-key="item => item.id"
          max-height="500px"
        >
          <template #email="{ item }">
            {{ item.email }}
          </template>

          <template #cardCode="{ item }">
            <code class="card-code">{{ item.cardCode }}</code>
          </template>

          <template #automationLoginEmail="{ item }">
            {{ item.automationLoginEmail || '-' }}
          </template>

          <template #activatedTime="{ item }">
            {{ formatDate(item.activatedTime) }}
          </template>

          <template #expireTime="{ item }">
            {{ formatDate(item.expireTime) }}
          </template>

          <template #status="{ item }">
            <span class="status-badge" :class="getActivationStatusClass(item.status)">
              {{ getActivationStatusText(item.status) }}
            </span>
            <div v-if="item.inviteErrorMsg" class="error-message">{{ item.inviteErrorMsg }}</div>
          </template>
        </AdminTable>

        <AdminPagination
          :current-page="activationCurrentPage"
          :page-size="activationPageSize"
          :total="totalActivations"
          @update:currentPage="activationCurrentPage = $event; loadAllActivations()"
          @update:pageSize="activationPageSize = $event; activationCurrentPage = 1; loadAllActivations()"
        />
      </section>

      <!-- 使用可复用的卡密管理组件 -->
      <CardManagementTable
        section-title="卡密列表"
        card-type-filter="member"
        :show-card-type-select="false"
        :show-batch-count="false"
        create-button-text="创建卡密"
        create-dialog-title="创建卡密"
        @message="handleMessage"
      />
    </div>
  </AdminLayout>
</template>

<script>
import AdminLayout from '@/components/admin/AdminLayout.vue'
import AdminTable from '@/components/admin/AdminTable.vue'
import AdminPagination from '@/components/admin/AdminPagination.vue'
import CardManagementTable from '@/components/admin/CardManagementTable.vue'
import {
  getActivations,
  searchActivations
} from '@/api/cards'

export default {
  name: 'CardManagement',
  components: {
    AdminLayout,
    AdminTable,
    AdminPagination,
    CardManagementTable
  },
  data() {
    return {
      activations: [],
      totalActivations: 0,
      searchEmail: '',
      // 激活记录分页
      activationCurrentPage: 1,
      activationPageSize: 20,
      activationColumns: [
        { key: 'email', title: '邮箱' },
        { key: 'cardCode', title: '卡密' },
        { key: 'automationLoginEmail', title: '自动化登录邮箱' },
        { key: 'activatedTime', title: '激活时间' },
        { key: 'expireTime', title: '到期时间' },
        { key: 'status', title: '状态' }
      ],
      message: '',
      messageType: 'success'
    }
  },
  mounted() {
    this.loadAllActivations()
  },
  methods: {
    async loadAllActivations() {
      try {
        const response = await getActivations({
          current: this.activationCurrentPage,
          size: this.activationPageSize,
          type: 'config'  // 只加载config类型的激活记录，过滤掉export类型
        })
        if (response.code === 200) {
          // 处理激活记录的分页数据
          if (response.data?.activations) {
            this.activations = response.data.activations || []
            this.totalActivations = response.data.total || 0
          } else {
            this.activations = response.data || []
            this.totalActivations = this.activations.length
          }
          console.log('Config Activations loaded:', this.activations.length, 'Total:', this.totalActivations)
        }
      } catch (error) {
        console.error('加载激活记录失败:', error)
      }
    },

    async searchActivationsByEmail() {
      if (!this.searchEmail.trim()) {
        this.loadAllActivations()
        return
      }

      try {
        const response = await searchActivations(this.searchEmail)
        if (response.code === 200) {
          // 搜索结果处理
          if (response.data?.activations) {
            this.activations = response.data.activations || []
            this.totalActivations = response.data.total || 0
          } else {
            this.activations = response.data || []
            this.totalActivations = this.activations.length
          }
          // 搜索时重置到第一页
          this.activationCurrentPage = 1
        } else {
          this.showMessage(response.message || '搜索激活记录失败', 'error')
        }
      } catch (error) {
        this.showMessage('搜索激活记录失败: ' + error.message, 'error')
      }
    },

    clearActivationSearch() {
      this.searchEmail = ''
      this.activationCurrentPage = 1
      this.loadAllActivations()
    },

    getActivationStatusText(status) {
      switch (status) {
        case 1: return '有效'
        case 2: return '已过期'
        case 3: return '激活中'
        case -1: return '激活失败'
        default: return '未知状态'
      }
    },

    getActivationStatusClass(status) {
      switch (status) {
        case 1: return 'status-active'
        case 2: return 'status-expired'
        case 3: return 'status-processing'
        case -1: return 'status-failed'
        default: return 'status-unknown'
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('zh-CN')
    },

    handleMessage(messageData) {
      this.showMessage(messageData.text, messageData.type)
    },

    showMessage(text, type = 'success') {
      this.message = text
      this.messageType = type
      setTimeout(() => {
        this.message = ''
      }, 3000)
    }
  }
}
</script>

<style scoped>
@import '@/styles/admin-common.css';

.card-management h2 {
  text-align: center;
  font-size: var(--font-size-xxl);
  margin-bottom: var(--spacing-xxl);
  color: var(--text-primary);
}

.card-management h3 {
  font-size: var(--font-size-xl);
  border-bottom: 2px solid var(--border-color);
  padding-bottom: var(--spacing-sm);
  margin-bottom: var(--spacing-xl);
  color: var(--text-primary);
}

.activations-section {
  margin-bottom: var(--spacing-xxl);
}

.card-code {
  background: var(--bg-primary);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-family: monospace;
  font-size: var(--font-size-sm);
  color: var(--primary-color);
  border: 1px solid var(--border-light);
}

.error-message {
  font-size: var(--font-size-xs);
  color: #666666;
  margin-top: 4px;
  word-break: break-all;
}

.activation-search {
  margin-bottom: var(--spacing-xl);
}

@media (max-width: 768px) {
  .card-management h2 {
    font-size: var(--font-size-xl);
    margin-bottom: var(--spacing-lg);
  }

  .activation-search {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-sm);
  }

  .activation-search input {
    flex: none;
    max-width: none !important;
  }

  .activation-search .btn {
    width: 100%;
  }

  /* 卡密代码在移动端的处理 */
  .card-code {
    font-size: var(--font-size-xs);
    padding: var(--spacing-xxs) var(--spacing-xs);
  }
}

@media (max-width: 480px) {
  .card-management {
    padding: var(--spacing-sm);
  }

  .card-management h2 {
    font-size: var(--font-size-lg);
  }

  .card-management h3 {
    font-size: var(--font-size-md);
  }
}
</style>